import React, { createContext, FC, useReducer } from 'react';

import { getLogger } from '@packages/core/logging';
import {
    ApiResponse,
    Asset,
    Qmr,
    CreateQmrDto,
    QmrResponseDto,
    PatchQmrDto,
    PatchQmrAssetDto,
    UpdateQmrCvtChallengeDetailsDto,
    UpdateQmrDtcCodesDto,
} from '@packages/models/api';

import { QmrsService } from './qmrs.service';
import { initialState, QmrActionDispatcher, qmrReducer, QmrState, qmrActions } from './qmrs.state';
import { getQmrEwdState } from './qmrs.utils';

const logger = getLogger('qmrs');

export const QmrStateContext = createContext<QmrState | undefined>(undefined);
export const QmrDispatchContext = createContext<QmrActionDispatcher | undefined>(undefined);

export const QmrProvider: FC<{ children: React.ReactNode; initialStateOverrides?: Partial<QmrState> }> = ({
    children,

    // Allows the initial state to be overridden, mostly for testing purposes
    initialStateOverrides = {},
}) => {
    const finalInitialState = {
        ...initialState,
        ...initialStateOverrides,
    };
    const [state, dispatch] = useReducer(qmrReducer, finalInitialState);

    return (
        <QmrStateContext.Provider value={state}>
            <QmrDispatchContext.Provider value={dispatch}>{children}</QmrDispatchContext.Provider>
        </QmrStateContext.Provider>
    );
};

interface QmrDataAccess {
    qmrsService: QmrsService;
    qmrDispatch: QmrActionDispatcher;
    ignoreCache?: boolean;
    signal?: AbortSignal;
}

export async function fetchQmr({
    clearLocalPatch = false,
    qmrId,
    qmrsService,
    qmrDispatch,
    signal,
    ignoreCache,
}: { clearLocalPatch?: boolean; qmrId: Qmr['qmrId'] } & QmrDataAccess) {
    if (clearLocalPatch) {
        qmrDispatch(qmrActions.applyLocalPatch(null));
    }

    return updateQmr({
        apiPromise: qmrsService.fetchQmr({
            qmrId,
            ignoreCache,
            signal,
        }),
        qmrDispatch,
    });
}

async function updateQmr({
    apiPromise,
    qmrDispatch,
}: {
    apiPromise: Promise<ApiResponse<QmrResponseDto>>;
    qmrDispatch: QmrActionDispatcher;
}) {
    const response = await apiPromise;

    if (!response.success && response.aborted) {
        return;
    } else if (!response.success) {
        return Promise.reject(response.data);
    }

    qmrDispatch(qmrActions.updateQmr({ qmr: response.data.qmr }));

    return response.data.qmr;
}

export async function createQmr({
    newQmr,
    qmrsService,
    qmrDispatch,
    signal,
}: { newQmr: CreateQmrDto } & QmrDataAccess) {
    return updateQmr({
        apiPromise: qmrsService.createQmr(newQmr, signal),
        qmrDispatch,
    });
}

export async function saveQmr({ qmr, qmrsService, qmrDispatch, signal }: { qmr: Qmr } & QmrDataAccess) {
    const {
        qmrId,
        retailerId,
        qmrStatus,
        vin,
        modelYear,
        carlineId,
        transmissionNumber,
        engineNumber,
        complaint,
        complaintDuplicated,
        repairOrderNumber,
        mileage,
        failureDate,
        cause,
        partNumber,
        correction,
        issueResolved,
        repairDate,
        dtcCodes,
    } = qmr;

    const ewdState = getQmrEwdState(qmr);

    const apiPromise = qmrsService.saveQmr(
        qmrId,
        {
            retailerId,
            vin,
            qmrStatusId: qmrStatus.qmrStatusId,
            modelYear,
            carlineId,
            transmissionNumber,
            engineNumber,
            complaint,
            repairOrderNumber,
            mileage,
            failureDate,
            cause,
            partNumber,
            correction,
            issueResolved,
            repairDate,
            ...ewdState,
            dtcCodesSet: true,
            dtcCodes: Array.isArray(dtcCodes)
                ? dtcCodes.map((c) => {
                      return {
                          dtcCodeId: c.dtcCodeId,
                          markedAsPrimary: !!c.markedAsPrimary,
                          freezeFrameDataAvailable: !!c.freezeFrameDataAvailable,
                      };
                  })
                : [],
            // failCode: null, // TODO: Fail Code Implementation
            complaintDuplicated: complaintDuplicated || false,
        },
        signal
    );

    return updateQmr({
        apiPromise,
        qmrDispatch,
    });
}

export async function patchQmr({
    qmrId,
    qmrPatch,
    isLocalPatch = false,
    qmrsService,
    qmrDispatch,
    signal,
}: {
    qmrId: Qmr['qmrId'];
    qmrPatch: Partial<PatchQmrDto>;
    isLocalPatch?: boolean;
} & QmrDataAccess) {
    if (isLocalPatch) {
        qmrDispatch(qmrActions.applyLocalPatch(qmrPatch));
        return;
    }

    return updateQmr({
        apiPromise: qmrsService.patchQmr({ qmrId, signal, ...qmrPatch }),
        qmrDispatch,
    });
}

export async function updateQmrDtcCodes({
    qmrId,
    dtcCodes,
    qmrsService,
    qmrDispatch,
    signal,
}: {
    qmrId: Qmr['qmrId'];
    dtcCodes: UpdateQmrDtcCodesDto['dtcCodes'];
} & QmrDataAccess) {
    return updateQmr({
        apiPromise: qmrsService.updateQmrDtcCodes(qmrId, { dtcCodesSet: true, dtcCodes }, signal),
        qmrDispatch,
    });
}

export async function updateQmrCvtChallengeDetails({
    qmrId,
    cvtChallengeDetails,
    qmrsService,
    qmrDispatch,
    signal,
}: {
    qmrId: Qmr['qmrId'];
    cvtChallengeDetails: UpdateQmrCvtChallengeDetailsDto;
} & QmrDataAccess) {
    return updateQmr({
        apiPromise: qmrsService.updateCvtChallengeDetails({ qmrId, cvtChallengeDetails, signal }),
        qmrDispatch,
    });
}

export async function updateQmrRestrictedAssetIds({
    qmrId,
    restrictedAssetIds,
    qmrsService,
    qmrDispatch,
    signal,
}: {
    qmrId: Qmr['qmrId'];
    restrictedAssetIds: Asset['assetId'][];
} & QmrDataAccess) {
    return updateQmr({
        apiPromise: qmrsService.updateQmrRestrictedAssetIds(
            qmrId,
            {
                restrictedAssetIds,
            },
            signal
        ),
        qmrDispatch,
    });
}

export async function updateQmrAsset({
    assetId,
    assetPatch,
    qmrsService,
    qmrDispatch,
    signal,
}: { assetId: Asset['assetId']; assetPatch: PatchQmrAssetDto } & QmrDataAccess) {
    try {
        const response = await qmrsService.patchQmrAsset({
            assetId,
            signal,
            ...assetPatch,
        });

        if (!response.success && response.aborted) {
            return;
        } else if (!response.success) {
            return Promise.reject(response.data);
        }

        qmrDispatch(qmrActions.updateAsset({ asset: response.data.asset }));

        return response.data.asset;
    } catch (e) {
        logger.error(`Failed to patch QMR asset [${assetId}]`, { assetId, error: e });
    }
}

export async function deleteQmrAssets({
    qmrId,
    assetIds,
    qmrsService,
    qmrDispatch,
    signal,
}: { qmrId: Qmr['qmrId']; assetIds: Asset['assetId'][] } & QmrDataAccess) {
    return updateQmr({
        apiPromise: qmrsService.deleteQmrAssets(qmrId, { assetIds }, signal),
        qmrDispatch,
    });
}

export async function deleteQmrCvtChallengeDetails({
    qmrId,
    qmrsService,
    qmrDispatch,
    signal,
}: {
    qmrId: Qmr['qmrId'];
} & QmrDataAccess) {
    return updateQmr({
        apiPromise: qmrsService.deleteCvtChallengeDetails({ qmrId, signal }),
        qmrDispatch,
    });
}

/**
 * Saves the CVT challenge held in `localPatch`, or deletes the saved one when given `null`. The challenge has its own
 * endpoint because the report PATCH does not accept it.
 *
 * It stays in `localPatch` after it is saved, so a late response can't bring back an older challenge, and every
 * later save sends it again. Resolves the updated report, or nothing if the request was cancelled.
 */
export async function saveQmrCvtChallengeDetails({
    qmrId,
    cvtChallengeDetails,
    qmrsService,
    qmrDispatch,
    signal,
}: {
    qmrId: Qmr['qmrId'];
    cvtChallengeDetails: UpdateQmrCvtChallengeDetailsDto | null;
} & QmrDataAccess) {
    if (cvtChallengeDetails === null) {
        return deleteQmrCvtChallengeDetails({ qmrId, qmrsService, qmrDispatch, signal });
    }

    return updateQmrCvtChallengeDetails({ qmrId, cvtChallengeDetails, qmrsService, qmrDispatch, signal });
}

export async function setQmrNoRepairModeFailCode({
    qmrId,
    locationCode,
    frequencyCode,
    concernCode,
    qmrsService,
    qmrDispatch,
    signal,
}: { qmrId: Qmr['qmrId']; locationCode: string; frequencyCode: string; concernCode: string } & QmrDataAccess) {
    return updateQmr({
        apiPromise: qmrsService.setQmrNoRepairModeFailCode({
            qmrId,
            locationCode,
            frequencyCode,
            concernCode,
            signal,
        }),
        qmrDispatch,
    });
}
